// Library imports
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Local imports
import { Button } from '@/components/Shared/Button';
import { TextInput } from '@/components/Shared/TextInput';
import { validatePassword, customToast, syncWpSessionInBackground } from '@/utils';
import { CustomSlideModal } from '@/components/Shared/KitModal/CustomModal';
import {
  setUserPasswordAPIFn,
  updateNewUser
} from '@/redux/features/CurrentUser/currentUserSlice';
import { CrossIcon } from '@/assets/icons/CrossIcon';

export function SetPasswordModal({
  open,
  onClose,
  setSampleKitToProfKit,
  onSavePasswordUpdateKit
}) {
  // REDUX
  const dispatch = useDispatch();
  const userInfo = useSelector(state => state?.currentUser?.currentUser);
  // STATES
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  // DERIVED PASSWORD VALIDATION
  const isPasswordValid = !validatePassword(formData?.password);
  const passwordsMismatch = !!formData?.confirmPassword && formData?.password !== formData?.confirmPassword;

  // HANLDE ONCHANGE
  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'password') {
      setErrors(prev => ({ ...prev, password: '' }));
    }

    if (name === 'confirmPassword') {
      setErrors(prev => ({ ...prev, confirmPassword: '' }));
    }
  };

  // HANDLE SUBMIT
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const { password, confirmPassword } = formData;
    const passwordMsg = validatePassword(password);

    if (passwordMsg) {
      setErrors(prev => ({ ...prev, password: passwordMsg }));
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setErrors({ confirmPassword: 'Passwords do not match.' });
      setIsLoading(false);
      return;
    }

    // CALL API
    await onSubmitPassword(confirmPassword);
    setIsLoading(false);
  };

  // ON SUBMIT PASSWORD
  const onSubmitPassword = async () => {
    const data = await dispatch(
      setUserPasswordAPIFn({
        email: userInfo?.email?.trim(),
        password: formData?.confirmPassword?.trim()
      })
    );

    if (data?.meta?.requestStatus === 'fulfilled') {
      syncWpSessionInBackground(userInfo?.email, formData?.confirmPassword);
      setSampleKitToProfKit(_prev => ({
        ..._prev,
        isSampleKit: false,
      }));

      // Close immediately — save the pending kit changes in the background.
      setIsLoading(false);
      onClose();
      onSavePasswordUpdateKit()
        .then(() => dispatch(updateNewUser(false)))
        .catch(() => {
          customToast.error('Failed to save your changes. Please try again.');
        });
    }

    if (data?.meta?.requestStatus === 'rejected') {
      customToast.error(data?.payload?.data?.message);
      setIsLoading(false);
    }
  };

  return (
    <CustomSlideModal
      open={open}
      radius={'0px'}
      mobilePadding={'0px'}
      mobileMaxHeight={'80%'}
    >
      <div className='flex items-center justify-between px-3 py-1 border-b border-black md:hidden'>
        <div className='w-7' />
        <p className="w-full text-lg font-light text-center font-notodisplay md:text-5xl">
          Set a Password
        </p>
        <CrossIcon onClick={onClose} circle={false} className='size-10' />
      </div>

      <form onSubmit={handleSubmit} className='vertical-center h-full pt-6 pb-4 overflow-y-auto bg-white md:py-6'>
        <p className="max-md:hidden w-full text-center font-notodisplay text-xl md:text-5xl font-light relative after:content-[''] md:after:block md:after:w-[15%] sm:after:w-[10%] after:h-[3px] after:bg-black after:mx-auto after:mt-2">
          Set a Password
        </p>

        <div className='vertical-center gap-10 px-4 mt-5 pb-6 md:p-10 md:pb-6 md:gap-14'>
          <p className='text-xl font-normal text-center font-baskervville sm:text-2xl text-neutral'>
            Preserve your Kit for later and set a password to protect it.
          </p>

          <div className='bg-white flex flex-col gap-5 max-w-[300px] w-full'>
            <TextInput
              name='password'
              onChange={handleChange}
              value={formData?.password}
              autoFocus
              type='password'
              label='Create Password'
              required
              error={!!errors.password}
              helperText={errors.password}
            />
            <TextInput
              name='confirmPassword'
              onChange={handleChange}
              value={formData?.confirmPassword}
              type='password'
              label='Confirm Password'
              required
              error={!!errors.confirmPassword || passwordsMismatch}
              helperText={errors.confirmPassword || (passwordsMismatch ? 'Passwords do not match.' : '')}
            />
          </div>
        </div>

        <p className='max-w-[300px] text-xs text-center font-raleway text-neutral-dark1'>
          Password must be at least 8 characters and include a lowercase letter, an uppercase letter, a number, and a symbol.
        </p>

        <div className='mt-auto max-md:sticky max-md:bottom-16 max-md:bg-white max-md:z-10 max-md:w-full max-md:flex max-md:justify-center md:mt-[2rem]'>
          <Button
            type='submit'
            variant='contained'
            color='primary.main'
            radius='4px'
            textColor='white'
            width={{
              xl: '260px',
              lg: '240px',
              md: '220px',
              smd: '200px',
              xxs: '180px'
            }}
            height={{ lg: '68px', md: '62px', xxs: '56px' }}
            className='!uppercase !border-[1px] !px-3 !border-solid !border-secondary-lightMintGreen !text-sm !font-normal !font-outfit !sm:text-base !rounded-[4px]'
            loading={isLoading}
            disabled={
              !isPasswordValid ||
              !formData?.confirmPassword ||
              passwordsMismatch ||
              isLoading
            }
          >
            Submit
          </Button>
        </div>
      </form>
    </CustomSlideModal>
  );
}
